/**
 * Client-side reliability telemetry.
 * ---------------------------------------------------------------------------
 * Converts low-level failures (RPC outages, proxy rate limiting, wallet
 * rejections) into anonymous analytics events so the /admin/reliability
 * dashboard can show conversion drops in real time.
 *
 * No PII is recorded: only the RPC method, an error class and the token.
 */
import { track } from "@/lib/analytics";
import { captureWarning } from "@/lib/monitoring";
import { RPC_FAILURE_EVENT, type RpcFailureDetail } from "@/lib/solana-rpc";

let installed = false;

/** Current degraded state, so widgets can render a graceful fallback. */
let degradedUntil = 0;

export function isRpcDegraded(): boolean {
  return Date.now() < degradedUntil;
}

/** Install the global RPC failure listener. Safe to call repeatedly. */
export function initReliabilityTelemetry(): void {
  if (installed || typeof window === "undefined") return;
  installed = true;
  window.addEventListener(RPC_FAILURE_EVENT, (event) => {
    const detail = (event as CustomEvent<RpcFailureDetail>).detail;
    if (!detail) return;
    if (detail.code === "rate_limited" || detail.code === "circuit_open") {
      degradedUntil = Date.now() + 15_000;
    }
    track("rpc_failure", {
      method: detail.method,
      code: detail.code,
      ...(detail.status ? { status: detail.status } : {}),
    });
    captureWarning(`RPC ${detail.code}`, { surface: "solana_rpc", method: detail.method });
  });
}

/** Report a wallet-level rejection or failure from the purchase flow. */
export function reportWalletFailure(
  code: string,
  info: { token: string; currency?: string; attr_source?: unknown; attr_token?: unknown },
): void {
  if (code === "rejected") {
    track("wallet_rejected", { ...info });
  }
  captureWarning(`wallet ${code}`, { surface: "wallet", ...info });
}
